/**
 * Frontend geocode UI types backed by canonical DTO contracts.
 */
import type { GeocodeTransit } from "./dtos/api/geocode.dto";

export interface Location {
    "lat": number;
    "lon": number;
}

export enum GeocodeSource {
    "PELIAS" = "PELIAS",
    "GOOGLE" = "GOOGLE",
    "MOTIS" = "MOTIS",
    "LOCAL" = "LOCAL",
    "MYLOCATION" = "MYLOCATION",
    "WEB" = "WEB",
}

export enum GeocodeType {
    "ALL" = "ALL",
    "ADDRESS" = "ADDRESS",
    "PLACE" = "PLACE",
    "STOP" = "STOP",
}

export enum GeocodeTransportMode {
    "WALK" = "WALK",
    "BIKE" = "BIKE",
    "RENTAL" = "RENTAL",
    "CAR" = "CAR",
    "CAR_PARKING" = "CAR_PARKING",
    "CAR_DROPOFF" = "CAR_DROPOFF",
    "ODM" = "ODM",
    "RIDE_SHARING" = "RIDE_SHARING",
    "FLEX" = "FLEX",
    "TRANSIT" = "TRANSIT",
    "TRAM" = "TRAM",
    "SUBWAY" = "SUBWAY",
    "FERRY" = "FERRY",
    "AIRPLANE" = "AIRPLANE",
    "SUBURBAN" = "SUBURBAN",
    "BUS" = "BUS",
    "COACH" = "COACH",
    "RAIL" = "RAIL",
    "HIGHSPEED_RAIL" = "HIGHSPEED_RAIL",
    "LONG_DISTANCE" = "LONG_DISTANCE",
    "NIGHT_RAIL" = "NIGHT_RAIL",
    "REGIONAL_FAST_RAIL" = "REGIONAL_FAST_RAIL",
    "REGIONAL_RAIL" = "REGIONAL_RAIL",
    "CABLE_CAR" = "CABLE_CAR",
    "FUNICULAR" = "FUNICULAR",
    "AERIAL_LIFT" = "AERIAL_LIFT",
    "OTHER" = "OTHER",
    "AREAL_LIFT" = "AREAL_LIFT",
    "METRO" = "METRO",
}

export interface GeocodeItem {
    "type":     GeocodeType;
    "id":       string;
    "result":   string;
    "source":   GeocodeSource;
    "location": {
        "coordinates": Location;
        "street"?:     string;
        "number"?:     string;
        "zip"?:        string;
        "timezone"?:   string;
        "country"?:    string;
    };
    "modes"?:    GeocodeTransportMode[];
    "category"?: {
        "name":  string;
        "icon"?: string;
    };
    "area"?: {
        "default": number;
        "tree":    Array<{
            "name":        string;
            "adminLevel"?: number;
        }>;
    };
    "transit"?: GeocodeTransit;
}

/**
 * Geocode request payload used by frontend geocode service methods.
 */
export interface GeocodeRequest {
    "text":  string;
    "place": {
        "lat": number;
        "lon": number;
    };
}

/**
 * Frontend-enriched geocode item used by search UI components.
 */
export interface SearchResultItem extends GeocodeItem {
    "title":    string;
    "subtitle": string;
    "icon": {
        "alt": string;
        "src": string;
    };
    "resume": string;
}

/**
 * Departure and arrival container used by forms and page state.
 */
export interface DepartureAndArrival {
    "departure":         Location;
    "arrival":           Location;
    "departureAddress"?: GeocodeItem;
    "arrivalAddress"?:   GeocodeItem;
}
